import { DTOBlock, DTORiskFactor } from '@/api/generated-catch-backend/model';
import NextArrow from '@/app/components/questionnaireV2/icons/next-arrow';
import ModalDetailRiskFactor from '@/components/modal-detail-risk-factor/modal-detail-risk-factor';
import useAnalyticsCalls, { DashboardSource } from '@/hooks/use-analytics';
import useRiskFactorModal from '@/hooks/use-risk-factor-modal';
import { RiskFactorType } from '@/repositories/risk.repository';
import { PortableText } from '@portabletext/react';

type RiskFactorLabel = 'decreasing' | 'increasing' | 'modifiable';

interface RiskFactorCardProps {
  riskFactor: DTORiskFactor;
  type: RiskFactorLabel;
  text: DTOBlock[];
  disableClick?: boolean;
}

const getRiskBadgeConfig = (type: RiskFactorLabel) => {
  switch (type) {
    case 'decreasing':
      return {
        label: 'Risk Decreasing',
        className: 'bg-[#7CF1C7] border-[#7CF1C7]',
      };
    case 'increasing':
      return {
        label: 'Risk Increasing',
        className: 'bg-[#FF9473] border-[#FF9473]',
      };
    case 'modifiable':
      return {
        label: 'Top Modifiable',
        className: 'bg-[#AFDFFF] border-[#AFDFFF]',
      };
  }
};

const getRiskFactorType = (riskFactor: DTORiskFactor): RiskFactorType => {
  switch (riskFactor.riskFactorType) {
    case 'quantifiable':
      return RiskFactorType.regular;
    case 'non-quantifiable-negative':
      return RiskFactorType.nonQuantifiableNegative;
    case 'non-quantifiable-positive':
      return RiskFactorType.nonQuantifiablePositive;
  }
};

export function RiskFactorCard({ riskFactor, type, text, disableClick }: RiskFactorCardProps) {
  const badgeConfig = getRiskBadgeConfig(type);
  const { setNavigationSource } = useAnalyticsCalls();

  const {
    handleOpenModal,
    onHideModalDetailRiskFactor,
    showModalDetailRiskFactor,
    selectedRiskFactor,
    affectedCancerTypes,
    impactsByCancerType,
  } = useRiskFactorModal();

  const openModal = !disableClick
    ? () => {
        // Track that this modal was opened from a dashboard risk factor card
        setNavigationSource(DashboardSource.DASHBOARD_SPECIFIC_RISK_FACTOR);

        handleOpenModal({
          id: riskFactor.slug,
          label: riskFactor.name,
          whyItMatters: riskFactor.whyItMatters,
          sources: riskFactor.sources,
          shortDescription: riskFactor.shortDescription,
          detailedDescription: '', // No detailed description from Sanity. Doesn't appear to be used by the modal
          impact: riskFactor.impact,
          riskFactorType: getRiskFactorType(riskFactor),
        });
      }
    : undefined;

  return (
    <>
      {!!selectedRiskFactor && (
        <ModalDetailRiskFactor
          isOpen={showModalDetailRiskFactor}
          onClose={onHideModalDetailRiskFactor}
          riskFactor={selectedRiskFactor}
          affectedCancerTypes={affectedCancerTypes}
          impactsByCancerType={impactsByCancerType}
        />
      )}
      <button
        className="relative h-full w-[240px] flex-shrink-0 rounded-[10px] border border-[#ededed] bg-white p-[18px_12px_11px_12px] transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-lg"
        onClick={openModal}
        style={{
          cursor: disableClick ? 'default' : 'pointer',
        }}
        aria-disabled={disableClick}
        data-testid={`risk-factor-card-${riskFactor.slug}`}
      >
        <div className="flex h-full flex-col items-start gap-4">
          <div className="flex w-full items-center justify-between gap-2">
            <div className={`flex min-h-[21px] min-w-0 rounded border px-2 py-[5px] ${badgeConfig.className}`}>
              <span className="truncate text-left text-[10px] leading-[1.3] font-semibold whitespace-nowrap text-[#183344]">
                {badgeConfig.label}
              </span>
            </div>
            <div
              className={`flex h-[39px] w-[39px] flex-shrink-0 items-center justify-center rounded-full bg-[#f0f5f6] text-[#183344] transition-colors duration-200 ease-in-out hover:bg-[#e0eaeb] ${disableClick ? 'hidden' : ''} `}
            >
              <NextArrow />
            </div>
          </div>

          <div className="flex w-full flex-col gap-1">
            <h3 className="text-left text-[16px] leading-[1.5] font-bold break-words text-[#101010]">
              {riskFactor.name}
            </h3>
            <p className="text-left text-sm leading-[1.4] font-normal break-words text-[#606060]">
              <PortableText
                value={text}
                components={{
                  block: {
                    normal: ({ children }) => <p>{children}</p>,
                  },
                  marks: {
                    strong: ({ children }) => <strong>{children}</strong>,
                  },
                }}
              />
            </p>
          </div>
        </div>
      </button>
    </>
  );
}
